import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowLeft, Eye } from 'lucide-react';
import { adminEnquetesAPI } from '../../utils/adminEnquetesAPI';
import PerguntaUnicaEscolha from '../../components/dynamic/PerguntaUnicaEscolha';
import PerguntaMultiplaEscolha from '../../components/dynamic/PerguntaMultiplaEscolha';
import PerguntaAberta from '../../components/dynamic/PerguntaAberta';

const COMPONENTE_POR_TIPO = {
  unica_escolha: PerguntaUnicaEscolha,
  multipla_escolha: PerguntaMultiplaEscolha,
  aberta: PerguntaAberta
};

// Preview de como a enquete aparece pro público, sem sair do painel admin.
// Reaproveita os mesmos componentes de pergunta do formulário público
// (components/dynamic/), mas nunca envia nada — o botão de enviar fica
// desabilitado. Existe porque, com a separação total entre admin e público
// (quem está logado não acessa mais /e/:slug), o preview precisa de um
// caminho próprio dentro do gerenciador.
const PreviewEnquete = () => {
  const { id } = useParams();
  const [enquete, setEnquete] = useState(null);
  const [loading, setLoading] = useState(true);
  const formMethods = useForm();

  useEffect(() => {
    adminEnquetesAPI.obter(id)
      .then(setEnquete)
      .catch(() => toast.error('Erro ao carregar preview'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="loading-spinner" /></div>;
  }

  if (!enquete) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Enquete não encontrada.</div>;
  }

  const perguntasAtivas = (enquete.perguntas || [])
    .filter((p) => p.ativa)
    .sort((a, b) => a.ordem - b.ordem);

  return (
    <div className="min-h-screen bg-corumba-light">
      <Toaster position="top-center" />

      <header className="bg-primary-600 text-white px-6 py-4">
        <Link to={`/admin/enquetes/${id}`} className="flex items-center gap-2 text-sm hover:underline mb-2">
          <ArrowLeft className="w-4 h-4" /> Voltar ao editor
        </Link>
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5" />
          <h1 className="text-lg font-bold">Pré-visualização: {enquete.titulo}</h1>
        </div>
        <p className="text-xs text-primary-100 mt-1">
          Isso é só uma prévia de como o formulário aparece pro público — nada aqui é enviado ou contado como voto.
        </p>
      </header>

      <main className="max-w-2xl mx-auto p-6">
        <FormProvider {...formMethods}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              toast('Pré-visualização: nenhum voto é registrado aqui.', { icon: 'ℹ️' });
            }}
            className="space-y-6"
          >
            {perguntasAtivas.length === 0 && (
              <p className="text-gray-500 text-center py-8">Nenhuma pergunta ativa ainda.</p>
            )}

            {perguntasAtivas.map((pergunta, index) => {
              const Componente = COMPONENTE_POR_TIPO[pergunta.tipo];
              if (!Componente) return null;
              return <Componente key={pergunta.id} pergunta={pergunta} indice={index + 1} />;
            })}

            {perguntasAtivas.length > 0 && (
              <div className="card text-center">
                <button
                  type="submit"
                  className="bg-gray-300 text-gray-600 font-medium py-2 px-6 rounded-lg cursor-not-allowed"
                >
                  Enviar (desabilitado na prévia)
                </button>
              </div>
            )}
          </form>
        </FormProvider>
      </main>
    </div>
  );
};

export default PreviewEnquete;
